import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { Account } from '@/model';
import { useTranslation } from 'react-i18next';

export interface AccountTableProps {
  data: Account[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: Account) => void;
  onDeleteClick?: (item: Account) => void;
}

export const AccountTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
}: AccountTableProps) => {
  const columnHelper = createColumnHelper<{
    created_at: string;
    first_name: string;
    card_number: string;
    last_access_at: string;
  }>();
  const { t } = useTranslation();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.accountlist.components.account_table.created_at'),
    }),

    columnHelper.accessor('first_name', {
      cell: props => props.getValue(),
      header: t('pages.accountlist.components.account_table.name'),
    }),
    columnHelper.accessor('card_number', {
      cell: props => props.getValue(),
      header: t('pages.accountlist.components.account_table.id'),
    }),
    columnHelper.accessor('last_access_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.accountlist.components.account_table.last_access'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.accountlist.components.account_table.actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.accountlist.components.account_table.aria_label_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Account)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text> {t('pages.accountlist.components.account_table.action_view_details')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
